// src/components/shared/ImageEditModal.js
import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { useData } from "../../providers/dataProvider";
const ImageEditModal = ({ show, onHide, imageId, onUpdateSuccess }) => {
  const { updateImage, getImageById } = useData();

  const [formData, setFormData] = useState({
    description: "",
    alt_text: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Load image data when modal opens
  useEffect(() => {
    if (show && imageId) {
      const imageData = getImageById(imageId);
      if (imageData) {
        setFormData({
          description: imageData.description || "",
          alt_text: imageData.alt_text || "",
        });
      }
    }
  }, [show, imageId, getImageById]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await updateImage(imageId, formData);
      setSuccess(true);

      // Call success callback if provided
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }

      // Auto-close after success
      setTimeout(() => {
        onHide();
        setSuccess(false);
      }, 1500);
    } catch (err) {
      setError(`Failed to update image: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      className="dark-modal"
    >
      <Modal.Header className="bg-dark text-light border-secondary">
        <Modal.Title>Edit Image Details</Modal.Title>
        <Button variant="close" className="btn-close-white" onClick={onHide} />
      </Modal.Header>

      <Modal.Body className="bg-dark text-light">
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-3">
            Image details updated successfully!
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter image description"
              className="bg-dark text-light border-secondary"
            />
            <Form.Text className="text-muted">
              A short description of what this image shows
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Alt Text</Form.Label>
            <Form.Control
              type="text"
              name="alt_text"
              value={formData.alt_text}
              onChange={handleChange}
              placeholder="Enter alt text for accessibility"
              className="bg-dark text-light border-secondary"
            />
            <Form.Text className="text-muted">
              Text that describes the image for screen readers
            </Form.Text>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="secondary" onClick={onHide} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ImageEditModal;
